import { Injectable } from '@angular/core';
import {
  BS_PERIOD_TYPES,
  BsPageData,
  ColumnOrder,
  MAX_DATE_COLUMNS_AMOUNT,
} from '../../component/bs-page/bs-page.component';
import {
  ColumnDate,
  ShownPeriodsData,
} from '../../utils/common/utilityClasses';
import { GeneralLedgerTransaction } from '../../models/common/GeneralLedgerTransaction';
import { FinancialStatementPart } from '../../models/common/FinancialStatementPart';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { FsCalculationsService } from '../common/fs-calculations.service';
import { FsPeriodsFormatterService } from '../common/fs-periods-formatter/fs-periods-formatter.service';
import { BsPeriodsSelectors } from '../../utils/bs-pl/utils';

@Injectable()

export class BsService {

  /**
   * Initializes the data required for the BsPageComponent, including setting up the periods data, periods selectors,
   * and selected balance sheet date columns based on the provided financial statement.
   *
   * It also calculates the balance sheet totals and updates the financial statement accordingly.
   *
   * The return type is structured to provide all necessary data for setting the values and rendering of
   * the balance sheet page effectively.
   *
   * @param fs {FinancialStatement} The financial statement data including the period, balance sheet parts, and general ledger (gl)
   *                                which are essential for initializing the balance sheet page data.
   * @returns An object containing the initialized data for the balance sheet page. This includes `periodsData`
   *                       for the available periods, `periodsSelectors` for the UI controls to select periods, `selectedBSDateColumns`
   *                       for the columns to display in the balance sheet.
   */
  initBsPageData(fs: FinancialStatement): BsPageData {
    if (!fs) {
      return;
    }
    const bsPageData = new BsPageData();
    bsPageData.periodsData = this.initBsPeriods(fs.balanceSheetParts);
    bsPageData.periodsSelectors = this.initializeBsPeriods(bsPageData.periodsData);
    bsPageData.selectedBSDateColumns = this.initDefaultBSDateColumns();
    this.setDefaultValuesForBSDateColumns(
      bsPageData.periodsSelectors.typeSelector.selected,
      bsPageData.selectedBSDateColumns,
      fs.period,
      bsPageData.periodsData,
      fs.gl,
    );

    FsCalculationsService.calculateBalanceSheetTotals(
      fs.balanceSheetNetTotals,
      fs.balanceSheetParts,
      false,
      this.getSelectedDates(bsPageData.selectedBSDateColumns),
    );

    return bsPageData;
  }

  /**
   * Generates an array of numbers representing the available number of shown periods for the financial statement.
   *
   * @param selectedBSPeriodType - The selected type of BS period (e.g., 'Quarters', 'Years').
   * @param periodsData - To obtain number of periods
   * @returns An array of numbers representing the available number of shown periods.
   */
  calculateBSPeriodsData(selectedBSPeriodType: (typeof BS_PERIOD_TYPES)[number], periodsData: ShownPeriodsData): number[] {
    const numberOfPeriods = selectedBSPeriodType === 'Quarters' ? periodsData.quarters.length : periodsData.years.length;
    let periodsArray = Array.from({ length: numberOfPeriods }, (_, i) => numberOfPeriods - i);
    // If no periods available, set default to a single period, showing only the last column (inception till reported period)
    if (!periodsArray.length) {
      periodsArray = [1];
    }
    return periodsArray;
  }

  /**
   * Sets the default values for the balance sheet date columns based on the selected balance sheet period type.
   *
   * @param selectedBSPeriodType The selected balance sheet period type ('Quarters' or 'Years').
   * @param selectedBSDateColumns
   * @param fsPeriod
   * @param periodsData
   * @param gl
   */
  setDefaultValuesForBSDateColumns(
    selectedBSPeriodType: string,
    selectedBSDateColumns: Map<ColumnOrder, ColumnDate>,
    fsPeriod: Date,
    periodsData: ShownPeriodsData,
    gl: GeneralLedgerTransaction[],
  ): void {
    const selectedPeriods = selectedBSPeriodType === 'Quarters'
      ? FsPeriodsFormatterService.formatQuartersWithStartAndEnd(periodsData.quarters)
      : FsPeriodsFormatterService.formatYearsWithStartAndEnd(periodsData.years, String(fsPeriod));
    if (selectedPeriods.length) {
      let idx = selectedPeriods.length - 1; // get index of last period
      // adjust the index if financialStatementPeriod is earlier than the end of the last period
      if (selectedPeriods[idx].end >= fsPeriod?.toString()) {
        idx--;
      }
      // reverse the array to assign dates from right to left
      const columnsArray = Array.from(selectedBSDateColumns).reverse();
      columnsArray.forEach(([columnName]) => {
        if (selectedPeriods[idx]) {
          this.changeBSDateColumn(columnName, selectedPeriods[idx--]?.end, selectedBSDateColumns);
        }
      });
    }
    // limitations: minDate for the first column and maxDate for the last column
    selectedBSDateColumns.get(ColumnOrder.FIRST).minDate =
      this.findEarliestTransactionDateMinusYear(gl);
    selectedBSDateColumns.get(ColumnOrder.THIRD).maxDate = String(fsPeriod);
  }

  changeBSDateColumn(dateColumn: ColumnOrder, selectedDate: string, selectedBSDateColumns: Map<ColumnOrder, ColumnDate>): void {
    selectedBSDateColumns.get(dateColumn).selectedDate = selectedDate;
    this.updateMinMaxDatesForSelectedDateColumns(dateColumn, selectedDate, selectedBSDateColumns);
  }

  getSelectedDates(selectedBSDateColumns: Map<ColumnOrder, ColumnDate>): string[] {
    if (!selectedBSDateColumns) {
      return [];
    }
    return Array.from(selectedBSDateColumns.values(), (column) => column.selectedDate);
  }

  private initBsPeriods(bsParts: FinancialStatementPart[]): ShownPeriodsData {
    const periodsData = new ShownPeriodsData();
    periodsData.quarters = FsPeriodsFormatterService.getShownQuarters(bsParts, MAX_DATE_COLUMNS_AMOUNT);
    periodsData.years = FsPeriodsFormatterService.getShownYears(bsParts, MAX_DATE_COLUMNS_AMOUNT);
    return periodsData;
  }

  private initializeBsPeriods(periodsData: ShownPeriodsData): BsPeriodsSelectors {
    const selectedBSPeriodType: (typeof BS_PERIOD_TYPES)[number] = BS_PERIOD_TYPES[0];
    const numberSelectorData = this.calculateBSPeriodsData(selectedBSPeriodType, periodsData);
    const numberOfShownBSPeriods = numberSelectorData[0];

    const periodsSelectors: BsPeriodsSelectors = {
      typeSelector: {
        data: BS_PERIOD_TYPES,
        selected: selectedBSPeriodType,
      },
      numberSelector: {
        data: numberSelectorData,
        selected: numberOfShownBSPeriods,
      },
    };

    return periodsSelectors;
  }

  /**
   * Updates the minDate and maxDate values for the selected date columns based on the changes in a specific date column.
   * Handles the cases if selectedDate is undefined, meaning that the date input was cleared.
   *
   * @param columnOrder The order of the date column that was changed.
   * @param selectedDate The newly selected date for the specified date column.
   * @param selectedBSDateColumns
   */
  private updateMinMaxDatesForSelectedDateColumns(columnOrder: ColumnOrder, selectedDate: string,
    selectedBSDateColumns: Map<ColumnOrder, ColumnDate>): void {

    const firstColumn = selectedBSDateColumns.get(ColumnOrder.FIRST);
    const secondColumn = selectedBSDateColumns.get(ColumnOrder.SECOND);
    const thirdColumn = selectedBSDateColumns.get(ColumnOrder.THIRD);

    switch (columnOrder) {
      case ColumnOrder.FIRST:
        secondColumn.minDate = selectedDate || firstColumn.minDate;
        thirdColumn.minDate = secondColumn.selectedDate || secondColumn.minDate;
        break;
      case ColumnOrder.SECOND:
        firstColumn.maxDate =
          selectedDate || thirdColumn.selectedDate || thirdColumn.maxDate;
        thirdColumn.minDate =
          selectedDate || firstColumn.selectedDate || firstColumn.minDate;
        break;
      case ColumnOrder.THIRD:
        secondColumn.maxDate = selectedDate || thirdColumn.maxDate;
        firstColumn.maxDate = secondColumn.selectedDate || secondColumn.maxDate;
        break;
      default:
        break;
    }
  }

  /**
   * Finds the earliest transaction date from the financial statement's general ledger and subtracts one year from it.
   * Used as minDate in the first selectedBSDateColumn.
   *
   * @returns A string representing the earliest transaction date minus one year in the format "YYYY-MM-DD".
   */
  private findEarliestTransactionDateMinusYear(gl: GeneralLedgerTransaction[]): string {
    const minDate = gl
      .map((transaction) => new Date(transaction.transactionDate))
      .sort((a, b) => a.valueOf() - b.valueOf())[0];
    // format "YYYY-MM-DD": 'year - 1' is Artem's request, 'month + 1' as jan is 0
    return `${minDate.getFullYear() - 1}-${(minDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${minDate.getDate().toString().padStart(2, '0')}`;
  }

  private initDefaultBSDateColumns(): Map<ColumnOrder, ColumnDate> {
    return new Map<ColumnOrder, ColumnDate>([
      [ColumnOrder.FIRST, { selectedDate: '', minDate: '', maxDate: '' }],
      [ColumnOrder.SECOND, { selectedDate: '', minDate: '', maxDate: '' }],
      [ColumnOrder.THIRD, { selectedDate: '', minDate: '', maxDate: '' }],
    ]);
  }
}
